import * as React from "react"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva } from "class-variance-authority";
import { X, CheckCircle2, AlertCircle, Info } from "lucide-react"

import { cn } from "@/lib/utils"

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      // Bottom on all breakpoints so toasts never cover fixed top CTAs
      // (checkout / sticky header). Right-aligned on desktop, full width
      // with safe-area bottom padding on mobile.
      "fixed bottom-0 left-0 right-0 z-[100] flex max-h-screen w-full flex-col gap-3 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:left-auto sm:top-auto sm:flex-col md:max-w-[400px]",
      className
    )}
    {...props} />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

// Editorial toast — cream ground, ink text, sharp corners with a terracotta
// (or muted-red for destructive) rule on the left as the accent. Matches the
// storefront's editorial vibe (see product cards / hero) instead of the
// default rounded rectangle bubble.
const toastVariants = cva(
  cn(
    "group pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden",
    "border p-4 pl-5 pr-8 shadow-[0_10px_30px_-12px_rgba(44,36,32,0.25)] backdrop-blur",
    "border-l-[3px]",
    "transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none",
    "data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out",
    "data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full",
    "data-[state=open]:slide-in-from-bottom-full"
  ),
  {
    variants: {
      variant: {
        default: "bg-[#fefcf7] text-[#2C2420] border-[#e8dfd2] border-l-[#be1f1a]",
        destructive: "bg-[#fefcf7] text-[#2C2420] border-[#e8dfd2] border-l-[#c14a3a]",
        success: "bg-[#fefcf7] text-[#2C2420] border-[#e8dfd2] border-l-[#7a8b6f]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

// Small square accent icon on the left of every toast — communicates
// state at a glance without shouting like the previous full-red bubble.
const ToastIcon = ({ variant }) => {
  if (variant === "destructive") {
    return (
      <AlertCircle
        className="h-5 w-5 shrink-0 mt-0.5"
        strokeWidth={1.75}
        style={{ color: "#c14a3a" }}
      />
    )
  }
  if (variant === "success") {
    return (
      <CheckCircle2
        className="h-5 w-5 shrink-0 mt-0.5"
        strokeWidth={1.75}
        style={{ color: "#7a8b6f" }}
      />
    )
  }
  return (
    <Info
      className="h-5 w-5 shrink-0 mt-0.5"
      strokeWidth={1.75}
      style={{ color: "#be1f1a" }}
    />
  )
}

const Toast = React.forwardRef(({ className, variant = "default", children, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}>
      <ToastIcon variant={variant} />
      <div className="flex-1 min-w-0">{children}</div>
    </ToastPrimitives.Root>
  );
})
Toast.displayName = ToastPrimitives.Root.displayName

const ToastAction = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center border border-[#2C2420] bg-transparent px-3 text-xs font-medium uppercase tracking-[0.15em] transition-colors hover:bg-[#2C2420] hover:text-white focus:outline-none focus:ring-1 focus:ring-[#be1f1a] disabled:pointer-events-none disabled:opacity-50",
      className
    )}
    {...props} />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "absolute right-2 top-2 rounded-full p-1 text-[#2C2420]/50 opacity-70 transition-all hover:text-[#2C2420] hover:opacity-100 hover:bg-[#2C2420]/5 focus:opacity-100 focus:outline-none focus:ring-1 focus:ring-[#be1f1a]",
      className
    )}
    toast-close=""
    {...props}>
    <X className="h-3.5 w-3.5" strokeWidth={2} />
  </ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn(
      "text-sm font-semibold leading-tight text-[#2C2420] [&+div]:mt-1",
      className
    )}
    {...props} />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("text-xs leading-relaxed text-[#2C2420]/70", className)}
    {...props} />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

export { ToastProvider, ToastViewport, Toast, ToastTitle, ToastDescription, ToastClose, ToastAction };
